import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    getFilteredRowModel,
    getExpandedRowModel,
    flexRender,
} from "@tanstack/react-table";
import React, { useState, useEffect, useRef } from "react";
import TablePaginationControls from "./TablePaginationControls";
import "./GenericTable.css";



export function GenericTable({ 
    columns, 
    data, 
    section,
    pageSize = 25,
    enablePagination = true,
    hasHeaderSorting = true,
    enableRowSelection = false,
    getRowId,
    rowSelection,
    defaultRowSelection = {},
    onRowSelectionChange,
    enableExpansion = false,
    expanded,
    onExpandedChange,
    getRowCanExpand,
    renderExpandedRow,
    rowClassName,
    elementIds = {},
    state,
    onSortingChange,
    enableSortingRemoval = false,
    enableOriginalOrderSort = true,
    enableColumnResizing = true,
    columnResizeMode = "onChange",
    meta,
    containerClassName = "",
    containerStyle = {},
    tableClassName = "",
    tableStyle = {},
    enforceMinRowsHeight = true,
    minVisibleRows = 5,
    enableRoundedCorners = false,
    cornerRadius = 8,
    onRowClick,
    paginationMode = "full",
}) {

    const [internalRowSelection, setInternalRowSelection] = useState(defaultRowSelection);
    const [internalSorting, setInternalSorting] = useState([]);
    const [internalPagination, setInternalPagination] = useState({ pageIndex: 0, pageSize });
    const [hasManualColumnSizing, setHasManualColumnSizing] = useState(false);
    const [containerWidth, setContainerWidth] = useState(0);

    // Keep internal pagination in sync when the pageSize prop changes.
    useEffect(() => {
        setInternalPagination(prev =>
            prev.pageSize === pageSize ? prev : { pageIndex: 0, pageSize }
        );
    }, [pageSize]);

    const isControlledSelection = rowSelection !== undefined;
    const isControlledSorting = state !== undefined && 'sorting' in (state || {});
    const resolvedSorting = isControlledSorting ? state.sorting : internalSorting;
    const handleSortingChange = onSortingChange || setInternalSorting;

    const resolvedRowSelection = isControlledSelection
        ? rowSelection
        : internalRowSelection;

    const handleRowSelectionChange = (updater) => {
        if (!enableRowSelection) {
            return;
        }

        if (!isControlledSelection) {
            setInternalRowSelection(updater);
        }

        if (onRowSelectionChange) {
            onRowSelectionChange(updater);
        }
    };

    // Allow a third sort state (unsorted) so users can return to the original row order.
    const allowSortReset = enableOriginalOrderSort || enableSortingRemoval;

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting: resolvedSorting,
            ...(state || {}),
            pagination: internalPagination,
            ...(enableRowSelection ? { rowSelection: resolvedRowSelection } : {}),
            ...(enableExpansion && expanded !== undefined ? { expanded } : {}),
        },
        enableRowSelection,
        onSortingChange: handleSortingChange,
        onPaginationChange: setInternalPagination,
        enableSortingRemoval: allowSortReset,
        ...(enableExpansion && getRowCanExpand ? { getRowCanExpand } : {}),
        onRowSelectionChange: enableRowSelection ? handleRowSelectionChange : undefined,
        onExpandedChange: enableExpansion ? onExpandedChange : undefined,
        getRowId,
        autoResetPageIndex: false,
        enableColumnResizing,
        columnResizeMode,
        meta,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
        getFilteredRowModel: getFilteredRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
    });

    const idPrefix = elementIds.prefix || section || "reusable-table";
    const resolveElementId = (key, fallback) => elementIds[key] || fallback;
    const rows = table.getRowModel().rows;
    const visibleColumns = table.getVisibleLeafColumns();
    const isEmptyState = rows.length === 0;
    const visibleBodyRowCount = rows.length;
    const footerGroups = table.getFooterGroups();
    const hasFooter = footerGroups.some((group) =>
        group.headers.some((header) => header.column.columnDef.footer !== undefined)
    );
    const placeholderRowCount = enforceMinRowsHeight
        ? Math.max(0, minVisibleRows - visibleBodyRowCount)
        : 0;
    const emptyStateHeight = enforceMinRowsHeight
        ? `${Math.max(minVisibleRows, 1) * 40}px`
        : undefined;
    const resolvedCornerRadius = typeof cornerRadius === "number" ? `${cornerRadius}px` : cornerRadius;
    const shouldRoundCorners = Boolean(enableRoundedCorners && resolvedCornerRadius);
    // Table width tracks the actual column total (rather than a forced 100%) so that resizing
    // one column never forces the browser to proportionally rescale the others to compensate.
    const resolvedTableWidth = enableColumnResizing
        ? `${Math.max(table.getTotalSize(), containerWidth)}px`
        : '100%';

    const scrollContainerRef = useRef(null);
    const hasManualColumnSizingRef = useRef(false);

    useEffect(() => {
        if (!enableColumnResizing) {
            return;
        }

        const containerEl = scrollContainerRef.current;
        if (!containerEl) {
            return;
        }

        const fillAvailableWidth = () => {
            const availableWidth = containerEl.clientWidth;
            setContainerWidth((currentWidth) =>
                currentWidth === availableWidth ? currentWidth : availableWidth
            );

            if (hasManualColumnSizingRef.current) {
                return;
            }

            const leafColumns = table.getVisibleLeafColumns();
            const totalDefaultWidth = leafColumns.reduce((sum, column) => sum + column.getSize(), 0);

            if (!availableWidth || totalDefaultWidth <= 0 || totalDefaultWidth >= availableWidth) {
                return;
            }

            const scaleFactor = availableWidth / totalDefaultWidth;
            const scaledSizing = {};
            leafColumns.forEach((column) => {
                scaledSizing[column.id] = Math.round(column.getSize() * scaleFactor);
            });
            table.setColumnSizing((prev) => ({ ...prev, ...scaledSizing }));
        };

        fillAvailableWidth();
        const resizeObserver = new ResizeObserver(fillAvailableWidth);
        resizeObserver.observe(containerEl);

        return () => resizeObserver.disconnect();
    }, [enableColumnResizing, table]);

    const beginManualColumnSizing = () => {
        hasManualColumnSizingRef.current = true;
        setHasManualColumnSizing(true);
    };

    const lastVisibleColumnId = visibleColumns[visibleColumns.length - 1]?.id;
    const remainingTableWidth = enableColumnResizing
        ? Math.max(0, containerWidth - table.getTotalSize())
        : 0;
    const getRenderedColumnSize = (column) =>
        column.getSize() + (column.id === lastVisibleColumnId ? remainingTableWidth : 0);

    // Auto-fit a column to the widest currently rendered cell content (header double-click).
    const handleAutoSizeColumn = (columnId, minSize) => {
        const cells = document.querySelectorAll(`[data-column-id="${columnId}"]`);
        let maxContentWidth = 0;
        cells.forEach((cell) => {
            maxContentWidth = Math.max(maxContentWidth, cell.scrollWidth);
        });
        const paddingBuffer = 24;
        const newSize = Math.max(maxContentWidth + paddingBuffer, minSize ?? 40);
        table.setColumnSizing((prev) => ({ ...prev, [columnId]: newSize }));
    };

    return (
        <div
            id={resolveElementId("container", `${idPrefix}-container`)}
            className={containerClassName}
            style={{ width: '100%', ...containerStyle }}
        >
            <div
                ref={scrollContainerRef}
                className={`table-scroll-container ${shouldRoundCorners ? "generic-table-rounded-container" : ""}`.trim()}
                style={{
                    width: '100%',
                    overflowX: 'auto',
                    ...(shouldRoundCorners ? { overflowY: 'hidden', borderRadius: resolvedCornerRadius } : {}),
                }}
            >
            <table
                id={resolveElementId("table", `${idPrefix}-main-table`)}
                className={`table table-hover generic-table ${rows.length === 0 ? "generic-table-empty-state" : ""} ${tableClassName}`.trim()}
                style={{
                    width: resolvedTableWidth,
                    ...(!enableColumnResizing || !hasManualColumnSizing ? { minWidth: '100%' } : {}),
                    marginBottom: 0,
                    textAlign: 'left',
                    ...(shouldRoundCorners ? { borderRadius: resolvedCornerRadius, overflow: 'hidden' } : {}),
                    ...tableStyle,
                }}
            >
                <thead id={resolveElementId("thead", `${idPrefix}-main-thead`)} className="table-header">
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr
                            key={headerGroup.id}
                            id={
                                typeof elementIds.headerRow === "function"
                                    ? elementIds.headerRow(headerGroup)
                                    : `${idPrefix}-header-row-${headerGroup.id}`
                            }
                        >
                            {headerGroup.headers.map((header, headerIndex) => {
                                const sortState = header.column.getIsSorted();
                                const isSortable = header.column.getCanSort();
                                const showSortIndicator = hasHeaderSorting && Boolean(sortState);
                                const isLastHeader = headerIndex === headerGroup.headers.length - 1;

                                return (
                                    <th 
                                        id={
                                            typeof elementIds.headerCell === "function"
                                                ? elementIds.headerCell(header)
                                                : `${idPrefix}-header-cell-${header.id}`
                                        }
                                        key={header.id} 
                                        className={sortState ? `generic-table-header-cell is-sorted is-sorted-${sortState}` : "generic-table-header-cell"}
                                        style={{
                                            cursor: isSortable ? "pointer" : "default",
                                            width: `${getRenderedColumnSize(header.column)}px`,
                                            textWrap: "auto",
                                        }}
                                        onClick={header.column.getToggleSortingHandler()}
                                        data-column-id={header.column.id}
                                    >
                                        {header.isPlaceholder 
                                            ? null
                                            : (
                                                <div className="generic-table-header-content">
                                                    <span className="generic-table-header-label">
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                    </span>
                                                    {showSortIndicator && (
                                                        <span
                                                            className={`generic-table-sort-indicator is-sorted-${sortState}`}
                                                            aria-hidden="true"
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        {enableColumnResizing && !isLastHeader && header.column.getCanResize() && (
                                            <div
                                                className={`generic-table-resizer ${header.column.getIsResizing() ? "is-resizing" : ""}`}
                                                onMouseDown={(e) => { e.stopPropagation(); beginManualColumnSizing(); header.getResizeHandler()(e); }}
                                                onTouchStart={(e) => { e.stopPropagation(); beginManualColumnSizing(); header.getResizeHandler()(e); }}
                                                onClick={(e) => e.stopPropagation()}
                                                onDoubleClick={(e) => {
                                                    e.stopPropagation();
                                                    beginManualColumnSizing();
                                                    handleAutoSizeColumn(header.column.id, header.column.columnDef.minSize);
                                                }}
                                            />
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody id={resolveElementId("tbody", `${idPrefix}-main-tbody`)} className="table-body">
                    {isEmptyState && (
                        <tr className="generic-table-empty-row" aria-live="polite">
                            <td
                                className="generic-table-empty-cell"
                                colSpan={Math.max(visibleColumns.length, 1)}
                                style={emptyStateHeight ? { height: emptyStateHeight, minHeight: emptyStateHeight } : undefined}
                            >
                                <div className="generic-table-empty-content" style={emptyStateHeight ? { height: emptyStateHeight, minHeight: emptyStateHeight } : undefined}>
                                    No rows found
                                </div>
                            </td>
                        </tr>
                    )}
                   
                         {rows.map((row) => (
                        <React.Fragment key={row.id}>
                            <tr
                                id={
                                    typeof elementIds.row === "function"
                                        ? elementIds.row(row)
                                        : `${idPrefix}-main-row-${row.id}`
                                }
                                className={typeof rowClassName === "function" ? rowClassName(row) : undefined}
                                onClick={typeof onRowClick === "function" ? () => onRowClick(row) : undefined}
                            >
                                {row.getVisibleCells().map((cell) => (
                                    (() => {
                                        const wrapClassName = cell.column.columnDef.meta?.wrap ? "cell-wrap" : "";
                                        const metaCellClassName = cell.column.columnDef.meta?.cellClassName || "";
                                        const tdClassName = [wrapClassName, metaCellClassName].filter(Boolean).join(" ");

                                        return (
                                    <td 
                                        id={
                                            typeof elementIds.cell === "function"
                                                ? elementIds.cell(cell)
                                                : `${idPrefix}-main-cell-${cell.id}`
                                        }
                                        key={cell.id}
                                        className={tdClassName || undefined}
                                        data-column-id={cell.column.id}
                                        style={{ 
                                            width: `${getRenderedColumnSize(cell.column)}px`, 
                                            textAlign: cell.column.columnDef.meta?.align ?? 'left',
                                            ...cell.column.columnDef.meta?.cellStyle
                                        }}
                                    >
                                        {flexRender(cell.column.columnDef.cell ?? cell.column.columnDef.header, cell.getContext())}
                                    </td>
                                        );
                                    })()
                                ))}
                            </tr>

                            {enableExpansion && typeof renderExpandedRow === "function" && row.getIsExpanded() && (
                                <tr
                                    id={
                                        typeof elementIds.expandedRow === "function"
                                            ? elementIds.expandedRow(row)
                                            : `${idPrefix}-main-expanded-row-${row.id}`
                                    }
                                    className="generic-table-expanded-row"
                                >
                                    <td
                                        id={
                                            typeof elementIds.expandedCell === "function"
                                                ? elementIds.expandedCell(row)
                                                : `${idPrefix}-main-expanded-cell-${row.id}`
                                        }
                                        colSpan={row.getVisibleCells().length}
                                    >
                                        {renderExpandedRow(row)}
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}

                    {Array.from({ length: isEmptyState ? 0 : placeholderRowCount }).map((_, index) => (
                        <tr
                            key={`${idPrefix}-placeholder-row-${index}`}
                            className="generic-table-placeholder-row"
                            aria-hidden="true"
                        >
                            {visibleColumns.map((column) => (
                                <td
                                    key={`${idPrefix}-placeholder-cell-${index}-${column.id}`}
                                    style={{ width: `${getRenderedColumnSize(column)}px` }}
                                >
                                    &nbsp;
                                </td>
                            ))}
                        </tr>
                    ))}
                       
                </tbody>

                {hasFooter && (
                    <tfoot id={resolveElementId("tfoot", `${idPrefix}-main-tfoot`)} className="table-footer">
                        {footerGroups.map((footerGroup) => (
                            <tr
                                key={footerGroup.id}
                                id={
                                    typeof elementIds.footerRow === "function"
                                        ? elementIds.footerRow(footerGroup)
                                        : `${idPrefix}-footer-row-${footerGroup.id}`
                                }
                            >
                                {footerGroup.headers.map((header) => (
                                    <td
                                        id={
                                            typeof elementIds.footerCell === "function"
                                                ? elementIds.footerCell(header)
                                                : `${idPrefix}-footer-cell-${header.id}`
                                        }
                                        key={header.id}
                                        style={{
                                            width: `${getRenderedColumnSize(header.column)}px`,
                                            textAlign: header.column.columnDef.meta?.align ?? 'left',
                                            ...header.column.columnDef.meta?.cellStyle,
                                        }}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                  header.column.columnDef.footer,
                                                  header.getContext()
                                              )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tfoot>
                )}
            </table>
            </div>

            {/* Pagination Controls */}
            {enablePagination && (
                <TablePaginationControls
                    table={table}
                    idPrefix={`${idPrefix}-main`}
                    elementIds={elementIds}
                    paginationMode={paginationMode}
                />
            )}
        </div>
    );
}
